import { ReactElement, useState } from 'react';
import classNames from 'classnames';
import { DialogPanel, DialogTitle } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useFormatDate } from 'hooks/useFormatDate';
import { QueryKey } from 'core/react-query/queryClient';
import { usePatchMemo } from 'networking/mindfeed/hooks/usePatchMemo';
import { DateTimePicker } from 'elements/DateTimePicker';
import { useModal } from 'xstate/hooks/useModal';
import { closeModal } from 'xstate/actors/ModalActor';
import { ChangeMemoDateModalPayload } from 'xstate/machines/ModalMachine';

// Pick a new date and time for a memo, as in the Mac app's Change Date
// sheet. Only the memo's `datedAt` changes.
const ChangeMemoDateModal = (): ReactElement | null => {
  const t = useTranslations();
  const formatDate = useFormatDate();
  const queryClient = useQueryClient();
  const { payload } = useModal() as { payload: ChangeMemoDateModalPayload };
  const memo = payload?.memo;

  const [selection, setSelection] = useState<Date>(() =>
    memo ? new Date(memo.datedAt) : new Date(),
  );

  const patchMemo = usePatchMemo({
    onSuccess: (updated) => {
      // A new date moves the memo in every list of its box and changes the
      // Journal's day counts, so they all reload.
      queryClient.invalidateQueries({
        queryKey: [QueryKey.MEMOS, updated.boxId],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.BOX_ACTIVITY, updated.boxId],
      });
      closeModal();
    },
  });

  if (!memo) {
    return null;
  }

  const current = new Date(memo.datedAt);

  const handleDone = () => {
    patchMemo.mutate({
      memo,
      updates: { datedAt: selection.toISOString() },
    });
  };

  return (
    <div className={styles.outer}>
      <div className={styles.inner}>
        <DialogPanel transition className={styles.panel}>
          <div className={styles.header.container}>
            <div className="min-w-0">
              <DialogTitle as="h2" className={styles.header.title}>
                {t('changeMemoDate.title')}
              </DialogTitle>
              <p className={styles.header.subtitle}>
                {t('changeMemoDate.current', {
                  date: formatDate(current, 'long'),
                  time: formatDate(current, 'time'),
                })}
              </p>
            </div>
            <button
              type="button"
              onClick={closeModal}
              aria-label={t('common.close')}
              className={styles.header.close}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className={styles.body}>
            <DateTimePicker
              id="change-date"
              value={selection}
              onChange={setSelection}
            />
          </div>

          <div className={styles.footer}>
            <button
              type="button"
              onClick={closeModal}
              className={styles.cancel}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleDone}
              disabled={patchMemo.isPending}
              className={styles.done}
            >
              {patchMemo.isPending ? t('common.saving') : t('common.done')}
            </button>
          </div>
        </DialogPanel>
      </div>
    </div>
  );
};

export default ChangeMemoDateModal;

const styles = {
  outer: 'fixed inset-0 z-10 w-screen overflow-y-auto',
  // Anchored near the top so the pickers, which open downward, have room.
  inner: 'flex min-h-full items-start justify-center p-4 pt-[12vh]',
  panel: classNames(
    'w-full max-w-[380px] rounded-2xl bg-surface-card text-ink-primary shadow-xl',
    'transition-all data-closed:opacity-0 data-closed:scale-95',
    'data-enter:duration-200 data-leave:duration-150',
  ),
  header: {
    container: 'flex items-start justify-between gap-3 px-6 pt-6 pb-3',
    title: 'truncate text-lg font-bold text-ink-primary',
    subtitle: 'text-sm text-ink-secondary',
    close:
      'flex h-8 w-8 flex-none items-center justify-center rounded-full bg-surface-field text-ink-secondary hover:bg-ink-secondary/15',
  },
  body: 'mx-6 rounded-xl border border-hairline',
  footer: 'flex items-center justify-end gap-2.5 px-6 pb-6 pt-4',
  cancel: classNames(
    'rounded-full bg-ink-secondary/12 px-4 py-2 text-sm font-medium text-ink-primary',
    'hover:bg-ink-secondary/20 focus:outline-hidden',
  ),
  done: classNames(
    'rounded-full bg-accent-fill px-4 py-2 text-sm font-semibold text-white',
    'hover:brightness-110 disabled:opacity-60 focus:outline-hidden',
  ),
};
